import React, { useState } from 'react';
import { X, Sparkles } from 'lucide-react';
import { generateTakeaway } from '../lib/groq';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

interface EndChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  messages: Array<{ sender: string; content: string }>;
  isAuthenticated: boolean;
  onSignUpClick: () => void;
  onTakeawayGenerated?: (takeaway: string) => void;
}

const EndChatModal: React.FC<EndChatModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  messages,
  isAuthenticated,
  onSignUpClick,
  onTakeawayGenerated,
}) => {
  const [takeaway, setTakeaway] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleGenerateTakeaway = async () => {
    try {
      setIsGenerating(true);
      setError(null);
      const takeawayContent = await generateTakeaway(messages);
      setTakeaway(takeawayContent);

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('reflections').insert({
          user_id: user.id,
          session_id: sessionId,
          content: takeawayContent
        });
      }
    } catch (error) {
      console.error('Error generating takeaway:', error);
      setError('Unable to generate takeaway. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="h-6 w-6" />
        </button>

        {isAuthenticated ? (
          !takeaway ? (
            <>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">
                Would you like to end the session?
              </h2>
              <p className="text-gray-600 mb-6">
                I'll keep your reflection updated in your dashboard — you can always return anytime.
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
                  {error}
                </div>
              )}

              <div className="flex space-x-4">
                <button
                  onClick={handleGenerateTakeaway}
                  disabled={isGenerating}
                  className="flex-1 py-3 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {isGenerating ? 'Generating...' : 'Yes, please'}
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  No, thanks
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-gray-800">
                Your Reflection
              </h2>
              <div className="space-y-6 max-h-[60vh] overflow-y-auto">
                {(() => {
                  const sections = takeaway.split('\n\n').filter(Boolean);
                  return (
                    <>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-2">Summary</h3>
                        <p className="text-gray-900 leading-relaxed">{sections[0] || ''}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-2">Reflection</h3>
                        <p className="text-gray-900 leading-relaxed">{sections[1] || ''}</p>
                      </div>
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4">
                        <h3 className="text-sm font-medium text-purple-700 mb-2">Key Takeaway</h3>
                        <p className="text-gray-900 leading-relaxed font-medium">{sections[2] || ''}</p>
                      </div>
                    </>
                  );
                })()}
              </div>
              <button
                onClick={() => {
                  if (onTakeawayGenerated) {
                    onTakeawayGenerated(takeaway);
                  }
                  navigate('/dashboard');
                }}
                className="w-full py-3 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                View in Dashboard
              </button>
            </div>
          )
        ) : (
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <Sparkles className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Want to save your reflections?
                </h3>
                <p className="text-gray-600">
                  Create a free account and we'll save your conversation. You'll get:
                </p>
                <ul className="mt-3 space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <span className="mr-2">•</span>
                    A thoughtful reflection from this chat
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">•</span>
                    Access to your chat history
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">•</span>
                    Personal dashboard to track your journey
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={onSignUpClick}
                className="flex-1 py-3 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Create Free Account
              </button>
              <button
                onClick={onClose}
                className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Maybe Later
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EndChatModal;